import React, { useState, useEffect, useMemo } from 'react';
import { KNOWN_LOGOS } from '../../hooks/useStockLogos';
import localLogos from '../../constants/localLogos.json';

// ฟังก์ชันจัดเตรียมลำดับแหล่ง Logo API กรณีอันแรกพัง ก็จะเปลี่ยนไปอันถัดไป
const getStockLogoUrls = (symbol, resolvedLogo) => {
  if (!symbol) return [];
  const cleanSymbol = symbol.split('-')[0].toUpperCase();
  const urls = [];

  // 0. โลโก้ที่ resolve มาแล้วจาก useStockLogos map (ถ้ามี)
  if (resolvedLogo) urls.push(resolvedLogo);

  // 1. โลโก้ออฟไลน์ที่ bundle มากับแอพ (ทำงานได้ทันที ไม่ต้องพึ่ง network/cache)
  if (localLogos.includes(cleanSymbol)) urls.push(`/logos/${cleanSymbol}.png`);

  // 2. โลโก้จากระบบที่รู้จักแล้ว (curated URL / inline SVG)
  if (KNOWN_LOGOS[cleanSymbol]) urls.push(KNOWN_LOGOS[cleanSymbol]);

  // 3. ลองหาจาก LocalStorage ที่ถูกดึงสำเร็จรูปแล้วด้วย useStockLogos (Finnhub)
  try {
    const cachedJSON = localStorage.getItem('investport_stock_logos_v2');
    if (cachedJSON) {
      const cachedLogos = JSON.parse(cachedJSON);
      if (cachedLogos[symbol]) urls.push(cachedLogos[symbol]);
      if (cachedLogos[cleanSymbol]) urls.push(cachedLogos[cleanSymbol]);
    }
  } catch (e) { }

  // 4. API อภิมหาอมตะจาก Financial Modeling Prep (ฟรี, ไม่ดัก Hotlink, มีแทบทุกตัวรวม Crypto)
  urls.push(`https://financialmodelingprep.com/image-stock/${cleanSymbol}.png`);

  // 5. ท่าไม้ตายสุดท้ายจาก Clearbit (มักโดน Adblock บล็อค แต่อาจจะใช้ได้ในบางเครื่อง)
  urls.push(`https://logo.clearbit.com/${cleanSymbol.toLowerCase()}.com`);

  // กำจัด URL ซ้ำ คงลำดับเดิมไว้
  return [...new Set(urls)];
};

// ฟังก์ชันสร้างโลโก้ placeholder สวยงาม
const createLogoPlaceholder = (symbol, size = 40) => {
  const colors = [
    'from-blue-500 to-blue-600',
    'from-green-500 to-emerald-500',
    'from-orange-500 to-red-500',
    'from-purple-500 to-purple-600',
    'from-indigo-500 to-blue-500'
  ];

  const colorIndex = symbol.charCodeAt(0) % colors.length;
  const gradient = colors[colorIndex];

  return {
    type: 'placeholder',
    symbol: symbol.substring(0, 2).toUpperCase(),
    gradient: gradient,
    size: size
  };
};

// Component สำหรับแสดงโลโก้หุ้นขั้นสูง (ระบบ Fallback ยืดหยุ่นสูงสุด)
const StockLogo = ({ symbol, size = 32, className = "", logos }) => {
  const cleanSymbol = symbol ? symbol.split('-')[0].toUpperCase() : '';
  const resolvedLogo = logos ? logos[symbol] : null;

  // สร้างคิวของ URL ที่จะทดสอบ
  const urls = useMemo(() => getStockLogoUrls(symbol, resolvedLogo), [symbol, resolvedLogo]);

  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    // รีเซ็ตเพื่อลองโหลด URL แรกสุดก่อนเสมอเมื่อเปลี่ยนหุ้น
    setSourceIndex(0);
  }, [symbol]);

  const activeUrl = urls[sourceIndex];

  // ถ้าลองโหลดจนครบทุก source แล้วพังหมด (หรือไม่มี url) ให้แสดง placeholder
  if (!activeUrl || sourceIndex >= urls.length) {
    const logoData = createLogoPlaceholder(cleanSymbol, size);
    return (
      <div
        className={`rounded-lg bg-gradient-to-br flex items-center justify-center text-white font-bold shadow-lg ${className}`}
        style={{
          width: size,
          height: size,
          backgroundImage: `linear-gradient(to bottom right, ${logoData.gradient.replace('from-', '#').replace('to-', '#')})`
        }}
      >
        <span style={{ fontSize: size / 4 }}>
          {logoData.symbol}
        </span>
      </div>
    );
  }

  // แสดงโลโก้จริง (ถ้าโหลดพัง จะสับไปลอง source ลำดับถัดไปทันที!)
  return (
    <div className={`relative flex items-center justify-center shrink-0 ${className}`} style={{ width: size, height: size }}>
      <img
        key={activeUrl} // สั่งให้ React ดึงรูปมา Render ใหม่เมื่อเปลี่ยน URL
        src={activeUrl}
        alt={`${cleanSymbol} logo`}
        className="w-full h-full rounded-md object-contain drop-shadow-sm dark:drop-shadow-[0_0_3px_rgba(255,255,255,0.3)] dark:brightness-110"
        onError={() => {
          // สลับ index ไประดับถัดไป
          setSourceIndex(prev => prev + 1);
        }}
      />
    </div>
  );
};

export { getStockLogoUrls, createLogoPlaceholder };
export default StockLogo;
